import type { ResaleListingGroup } from './resale-listing.context';

/**
 * Resale price (without fee) compared to the ticket type's original price,
 * computed by the backend: `below` < original, `equal` = original, `above` > original.
 */
export type PriceComparison = 'below' | 'equal' | 'above';

const PRICE_COMPARISONS: ReadonlyArray<PriceComparison> = ['below', 'equal', 'above'];

type PricingFields = 'serviceFeePct' | 'serviceFee' | 'totalPrice' | 'priceComparison';

/**
 * A `detail.groups` item as received from `/grouped`: the new pricing fields
 * are not validated yet (a legacy backend omits them; a buggy backend may
 * send anything).
 */
export type RawResaleListingGroup = Omit<ResaleListingGroup, PricingFields> & {
  [K in PricingFields]?: unknown;
};

function toMoney(value: unknown): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value) || value < 0) return undefined;
  return value;
}

function toPriceComparison(value: unknown): PriceComparison | null | undefined {
  if (value === null) return null;
  return PRICE_COMPARISONS.find((literal) => literal === value);
}

/**
 * Validates the backend-computed pricing fields at the fetch boundary.
 *
 * The front end NEVER computes fee or total: it only displays what it got. So an
 * invalid value is not corrected but dropped (`undefined`), and the UI degrades —
 * without fee/total it shows only the price; without a comparison, no badge. Each
 * field is decided on its own. `priceComparison: null` is valid (no original price).
 */
export function sanitizeResaleListingGroup(raw: RawResaleListingGroup): ResaleListingGroup {
  const { serviceFeePct, serviceFee, totalPrice, priceComparison, ...rest } = raw;

  return {
    ...rest,
    serviceFeePct : toMoney(serviceFeePct),
    serviceFee : toMoney(serviceFee),
    totalPrice : toMoney(totalPrice),
    priceComparison : toPriceComparison(priceComparison),
  };
}
